import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
import { setAccessToken } from 'lib/auth'
import { useDispatch, useSelector } from 'react-redux'
import { getFormValues, InjectedFormProps, reduxForm } from 'redux-form'
// hooks
import { useSnackbar } from 'notistack'
import { useShowModal } from 'hooks/useShowModal'
import { useMicrosoftLogin } from 'hooks/useMicrosoftLogin'
// components
import { ReduxInput } from 'components/FormElements/ReduxForm'
import { InputAdornment } from '@mui/material'
import { Button, ErrorMessage, Link } from 'components/UI'
// view components
import TwoFactorSetupModal from 'components/Modals/TwoFactorSetupModal'
import AuthCompanySelectModal from 'components/Modals/AuthCompanySelectModal'
// constants
import { Forms } from 'constants/forms'
import { Modals } from 'constants/modals'
// types
import { IAuthUserWithToken } from 'types/graphql'
// graphql
import { AUTH_SIGN_IN_MUTATION } from 'graphql/auth/mutations'
// redux
import { setCurrentUser } from 'store/reducers/currentUser'
import { setTwoFactorToken } from 'store/reducers/twoFactorAuth'
// views
import { AuthForm, AuthHeader, ButtonWrapper, MicrosoftLoginButton } from 'themes/views/auth'
// static
import MicrosoftIcon from 'src/icons/ms.svg'
// types
import { AuthErrorCode } from 'types/auth/helpers'
// utils
import { requiredEmpty } from 'utils/validators'
import { handleSafeGraphQLErrors } from 'utils/errors/parsers'
import { clearRedirectUrl, getRedirectUrl } from 'utils/cookies'

const ALLOWED_CODES = [
  AuthErrorCode.UNAUTHENTICATED,
  AuthErrorCode.BAD_USER_INPUT,
  AuthErrorCode.RATE_LIMIT_EXCEEDED,
  AuthErrorCode.TWO_FACTOR_REQUIRED
]

type FormData = {
  email: string
  domain: string
  password: string
}

type AuthSignInResponse = { authSignIn: { data: IAuthUserWithToken } }

const SignIn: React.FC<InjectedFormProps<FormData>> = ({ error, change, handleSubmit }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const [signInMutation] = useMutation<AuthSignInResponse>(AUTH_SIGN_IN_MUTATION)

  const { enqueueSnackbar } = useSnackbar()

  const { showModal: showSetupModal } = useShowModal({ modalType: Modals.TWO_FACTOR_SETUP_MODAL })

  const { microsoftSignIn } = useMicrosoftLogin()

  const formValues = useSelector((state) => getFormValues(Forms.SIGN_IN_FORM)(state)) as FormData

  const email: string = formValues?.email
  const domain: string = formValues?.domain

  const handleLogin = async (formData: FormData) => {
    try {
      const {
        data: {
          authSignIn: { data }
        }
      } = await signInMutation({
        variables: {
          email: formData.email.trim(),
          domain: `${formData.domain}.skefto.com`,
          password: formData.password.trim()
        }
      })

      // Check if 2FA is required
      if (data.twoFactorRequired && data.twoFactorToken) {
        // Store token in Redux (memory only, expires in 5 minutes)
        dispatch(
          setTwoFactorToken({
            token: data.twoFactorToken,
            expiryMinutes: 5
          })
        )

        // Redirect to 2FA verification page
        await router.push('/login/verify-2fa')
        return
      }

      // 2FA setup is required (company policy enforced)
      if (data.twoFactorSetupRequired && data.twoFactorSetupToken) {
        showSetupModal({
          setupToken: data.twoFactorSetupToken,
          onSuccess: () =>
            enqueueSnackbar('Two-factor authentication has been set up. Please log in again.', { variant: 'info' })
        })
        return
      }

      // Regular login flow (no 2FA)
      dispatch(setCurrentUser(data.user))
      setAccessToken(data.accessToken)

      const redirectUrl = getRedirectUrl()

      if (redirectUrl) {
        clearRedirectUrl()

        await router.push(redirectUrl)
      } else {
        await router.push('/home')
      }
    } catch (errors: any) {
      handleSafeGraphQLErrors({
        allowedCodes: ALLOWED_CODES,
        error: errors,
        logPrefix: '[SignIn.handleLogin]'
      })
    }
  }

  React.useEffect(() => {
    if (router.query.domain) {
      change('domain', decodeURI(router.query.domain as any))
    }
  }, [router.query])

  return (
    <>
      <AuthHeader>LOGIN TO YOUR ACCOUNT</AuthHeader>
      <AuthForm onSubmit={handleSubmit(handleLogin)}>
        <ReduxInput
          type='text'
          name='domain'
          label='Domain'
          validate={requiredEmpty}
          endAdornment={<InputAdornment position='end'>.skefto.com</InputAdornment>}
        />
        <ReduxInput type='text' name='email' label='Email or Username' validate={[requiredEmpty]} />
        <div>
          <ReduxInput type='password' name='password' label='Password' validate={requiredEmpty} />
          <Link
            href={{
              pathname: '/reset-password',
              query: {
                ...(email && { email }),
                ...(domain && { domain })
              }
            }}>
            Forgot or change password
          </Link>
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        <ButtonWrapper>
          <Button variant='contained' fullWidth type='submit'>
            LOGIN
          </Button>
          <span />
          <Button variant='contained' fullWidth type='button' onClick={microsoftSignIn}>
            <MicrosoftLoginButton>
              <MicrosoftIcon />
              login with microsoft
            </MicrosoftLoginButton>
          </Button>
        </ButtonWrapper>
      </AuthForm>

      <TwoFactorSetupModal />
      <AuthCompanySelectModal />
    </>
  )
}

export default reduxForm<FormData>({ form: Forms.SIGN_IN_FORM })(SignIn)
